import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FileText, FileUp, HelpCircle, Info, RefreshCw } from "lucide-react";
import {
  ClientPlanDocumentsApiError,
  PatientPlanDocumentSummary,
  PlanDocument,
  PlanType,
  clientPlanDocumentsApi,
} from "../api/clientPlanDocumentsApi";
import type { ClientAccessScope } from "../api/contracts";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import { formatPortalDateTime } from "../utils/dateTime";
import { ActionsColumnHeader, ContentSection } from "./DataDisplay";
import { LoadingState } from "./FeedbackStates";
import { RowActionMenu } from "./HomeworkControls";

const planDefinitions: readonly {
  planType: PlanType;
  displayName: string;
  helpText: string;
  helpExamples?: readonly string[];
}[] = [
  {
    planType: "safety_plan",
    displayName: "Safety Plan",
    helpText:
      "Once uploaded, the client can query their own Safety Plan using OCD Homework Assistant.",
    helpExamples: [
      "What are the warning signs listed in my safety plan?",
      "According to my safety plan, what situations or triggers should I watch for?",
    ],
  },
  {
    planType: "exposure_hierarchy",
    displayName: "Exposure Hierarchy",
    helpText:
      "Once uploaded, the client can query their own Exposure Hierarchy using OCD Homework Assistant.",
  },
  {
    planType: "relapse_prevention",
    displayName: "Relapse Prevention Plan",
    helpText:
      "Once uploaded, the client can query their own Relapse Prevention Plan using OCD Homework Assistant.",
  },
  {
    planType: "cope_ahead",
    displayName: "Cope Ahead Plan",
    helpText:
      "Once uploaded, the client can query their own Cope Ahead Plan using OCD Homework Assistant.",
  },
];

const PLAN_DOCUMENT_MIME_TYPES = new Map<string, string>([
  [".pdf", "application/pdf"],
  [
    ".docx",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  [".txt", "text/plain"],
]);
const MAX_PLAN_DOCUMENT_BYTES = 10 * 1024 * 1024;
const PLAN_DOCUMENT_REPLACEMENT_GUIDANCE =
  "Choose Replace and upload the new version. The current document remains available until the new version is successfully indexed. Only one document is available to the assistant at a time.";
const PLAN_DOCUMENT_COLUMN_WIDTHS_STORAGE_KEY =
  "clinician-portal-plan-document-column-widths";
const PLAN_DOCUMENT_COLUMN_DEFAULTS = {
  document: 260,
  status: 120,
} as const;
const PLAN_DOCUMENT_COLUMN_LIMITS = {
  document: { min: 200, max: 420 },
  status: { min: 92, max: 220 },
} as const;
const PLAN_DOCUMENT_KEYBOARD_RESIZE_STEP = 10;

type ResizablePlanDocumentColumn = keyof typeof PLAN_DOCUMENT_COLUMN_DEFAULTS;
type PlanDocumentColumnWidths = Record<ResizablePlanDocumentColumn, number>;

function clampPlanDocumentColumnWidth(
  column: ResizablePlanDocumentColumn,
  width: number,
): number {
  const limits = PLAN_DOCUMENT_COLUMN_LIMITS[column];
  return Math.min(limits.max, Math.max(limits.min, Math.round(width)));
}

function getInitialPlanDocumentColumnWidths(): PlanDocumentColumnWidths {
  if (typeof window === "undefined") return { ...PLAN_DOCUMENT_COLUMN_DEFAULTS };

  try {
    const savedValue = window.localStorage.getItem(
      PLAN_DOCUMENT_COLUMN_WIDTHS_STORAGE_KEY,
    );
    if (!savedValue) return { ...PLAN_DOCUMENT_COLUMN_DEFAULTS };
    const savedWidths = JSON.parse(savedValue) as Partial<
      Record<ResizablePlanDocumentColumn, unknown>
    >;

    return {
      document:
        typeof savedWidths.document === "number"
          ? clampPlanDocumentColumnWidth("document", savedWidths.document)
          : PLAN_DOCUMENT_COLUMN_DEFAULTS.document,
      status:
        typeof savedWidths.status === "number"
          ? clampPlanDocumentColumnWidth("status", savedWidths.status)
          : PLAN_DOCUMENT_COLUMN_DEFAULTS.status,
    };
  } catch {
    return { ...PLAN_DOCUMENT_COLUMN_DEFAULTS };
  }
}

function PlanDocumentColumnResizeHandle({
  column,
  label,
  width,
  onChange,
}: {
  column: ResizablePlanDocumentColumn;
  label: string;
  width: number;
  onChange: (width: number) => void;
}) {
  const [active, setActive] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(
    null,
  );
  const bodyStyleRef = useRef<{ cursor: string; userSelect: string } | null>(
    null,
  );
  const limits = PLAN_DOCUMENT_COLUMN_LIMITS[column];

  const restoreBodyStyles = useCallback(() => {
    if (!bodyStyleRef.current) return;
    document.body.style.cursor = bodyStyleRef.current.cursor;
    document.body.style.userSelect = bodyStyleRef.current.userSelect;
    bodyStyleRef.current = null;
  }, []);

  const finishResize = useCallback(() => {
    dragStateRef.current = null;
    setActive(false);
    restoreBodyStyles();
  }, [restoreBodyStyles]);

  useEffect(() => () => restoreBodyStyles(), [restoreBodyStyles]);

  return (
    <div
      className="cp-plan-document-column-resize-handle"
      data-active={active || undefined}
      role="separator"
      aria-label={`Resize ${label} column`}
      aria-orientation="vertical"
      aria-valuemin={limits.min}
      aria-valuemax={limits.max}
      aria-valuenow={width}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        dragStateRef.current = {
          startX: event.clientX,
          startWidth: width,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setActive(true);
        bodyStyleRef.current = {
          cursor: document.body.style.cursor,
          userSelect: document.body.style.userSelect,
        };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      }}
      onPointerMove={(event) => {
        if (!dragStateRef.current) return;
        event.preventDefault();
        onChange(
          dragStateRef.current.startWidth +
            event.clientX -
            dragStateRef.current.startX,
        );
      }}
      onPointerUp={finishResize}
      onPointerCancel={finishResize}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange(PLAN_DOCUMENT_COLUMN_DEFAULTS[column]);
      }}
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        event.stopPropagation();
        onChange(
          width +
            (event.key === "ArrowRight" ? 1 : -1) *
              PLAN_DOCUMENT_KEYBOARD_RESIZE_STEP,
        );
      }}
    />
  );
}

function PlanDocumentHelp({
  definition,
}: {
  definition: (typeof planDefinitions)[number];
}) {
  const tooltipId = React.useId();

  return (
    <button
      type="button"
      className="cp-homework-overview-tooltip"
      aria-label={"About " + definition.displayName}
      aria-describedby={tooltipId}
    >
      <HelpCircle aria-hidden="true" />
      <span
        id={tooltipId}
        className="cp-homework-overview-tooltip__content"
        role="tooltip"
      >
        <strong>{definition.helpText}</strong>
        {definition.helpExamples ? (
          <>
            {" "}For example, the client can ask, “
            <em>{definition.helpExamples[0]}</em>
            ”, and “<em>{definition.helpExamples[1]}</em>”
          </>
        ) : null}
      </span>
    </button>
  );
}

function ExposureHierarchyTextHelp({
  variant,
}: {
  variant: "guidance" | "example";
}) {
  const tooltipId = React.useId();
  const isExample = variant === "example";

  return (
    <button
      type="button"
      className="cp-homework-overview-tooltip cp-exposure-hierarchy-text__tooltip"
      aria-label={
        isExample
          ? "Example Exposure Hierarchy"
          : "About Exposure Hierarchy Text"
      }
      aria-describedby={tooltipId}
    >
      {isExample ? (
        <FileText aria-hidden="true" />
      ) : (
        <HelpCircle aria-hidden="true" />
      )}
      <span
        id={tooltipId}
        className="cp-homework-overview-tooltip__content cp-exposure-hierarchy-text__tooltip-content"
        data-variant={variant}
        role="tooltip"
      >
        {isExample ? (
          <>
            <strong className="cp-exposure-hierarchy-text__example-title">
              Example only — Fear Hierarchy
            </strong>
            <span className="cp-exposure-hierarchy-text__example-level">
              <strong>10:</strong>
              <span>Eating off the floor</span>
              <span>Licking a shoe</span>
              <span>Holding a knife to Mom’s throat</span>
              <span>
                Saying sexual obsessions aloud without engaging in mental
                compulsions
              </span>
            </span>
            <span className="cp-exposure-hierarchy-text__example-level">
              <strong>9:</strong>
              <span>Eating off the table</span>
              <span>Not washing hands before entering a room</span>
            </span>
          </>
        ) : (
          <>
            Preserve the hierarchy levels and exposure descriptions. The Homework
            Progress Report uses this text to assess how completed and attempted
            homework aligns with the client’s exposure hierarchy. Results appear
            in the “
            <strong>Homework Alignment with Exposure Hierarchy</strong>” section.
          </>
        )}
      </span>
    </button>
  );
}

export function planDocumentValidationMessage(file: File): string | null {
  const lowerName = file.name.toLocaleLowerCase();
  const extension = [...PLAN_DOCUMENT_MIME_TYPES.keys()].find((value) =>
    lowerName.endsWith(value),
  );
  if (!extension || PLAN_DOCUMENT_MIME_TYPES.get(extension) !== file.type) {
    return `${file.name} must be a PDF, DOCX, or TXT file.`;
  }
  if (file.size > MAX_PLAN_DOCUMENT_BYTES) {
    return `${file.name} must be 10 MiB or smaller.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  return null;
}

export function planDocumentFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The document could not be read."));
    reader.onload = () => {
      const value = typeof reader.result === "string" ? reader.result : "";
      const separator = value.indexOf(",");
      if (separator < 0) {
        reject(new Error("The document could not be read."));
        return;
      }
      resolve(value.slice(separator + 1));
    };
    reader.readAsDataURL(file);
  });
}

const inFlightLists = new Map<string, ReturnType<typeof clientPlanDocumentsApi.list>>();

function listPlanDocumentsOnce(
  clinicianCode: string,
  patientId: string,
  accessScope: ClientAccessScope,
) {
  const key = `${clinicianCode}:${patientId}:${accessScope}`;
  const existing = inFlightLists.get(key);
  if (existing) return existing;
  const listRequest =
    accessScope === "team"
      ? clientPlanDocumentsApi.list(
          clinicianCode,
          patientId,
          undefined,
          accessScope,
        )
      : clientPlanDocumentsApi.list(clinicianCode, patientId);
  const request = listRequest.finally(() => {
    if (inFlightLists.get(key) === request) inFlightLists.delete(key);
  });
  inFlightLists.set(key, request);
  return request;
}

function formatUpdatedAt(value: string): string {
  if (!value.trim()) return "";
  const formatted = formatPortalDateTime(value);
  return formatted === "—" ? "" : formatted;
}

function statusPresentation(document?: PlanDocument): {
  label: string;
  tone: "positive" | "neutral" | "failure";
} {
  if (!document) return { label: "Not added", tone: "neutral" };
  if (document.status === "failed") {
    return { label: "Failed", tone: "failure" };
  }
  if (document.status === "processing") {
    return { label: "Processing", tone: "neutral" };
  }
  if (document.status === "ready" && document.indexed) {
    return { label: "Indexed", tone: "positive" };
  }
  if (document.status === "ready") {
    return { label: "Ready", tone: "positive" };
  }
  return { label: document.status || "Not added", tone: "neutral" };
}

type ConfirmationState =
  | { kind: "replace"; file: File }
  | { kind: "delete" }
  | null;

function PlanDocumentConfirmationDialog({
  confirmation,
  displayName,
  onCancel,
  onConfirm,
}: {
  confirmation: Exclude<ConfirmationState, null>;
  displayName: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => cancelRef.current?.focus(), []);

  const replacing = confirmation.kind === "replace";
  return (
    <div
      className="cp-confirm-dialog__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        className="cp-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
      >
        <h2 id={titleId}>
          {replacing ? "Replace" : "Delete"} the {displayName}?
        </h2>
        <p id={descriptionId}>
          {replacing
            ? "The current document will remain available until the new document is successfully indexed."
            : "This will remove the document from the client’s plan documents and from the OCD Homework Assistant’s indexed knowledge."}
        </p>
        <div className="cp-confirm-dialog__actions">
          <button
            ref={cancelRef}
            type="button"
            className="cp-button cp-button--secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`cp-button ${
              replacing ? "cp-button--primary" : "cp-button--danger"
            }`}
            onClick={onConfirm}
          >
            {replacing ? "Replace Document" : "Delete Document"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanDocumentDropzone({
  displayName,
  disabled,
  onFiles,
  onCancel,
}: {
  displayName: string;
  disabled: boolean;
  onFiles: (files: readonly File[]) => void;
  onCancel?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const handleSelection = (event: ChangeEvent<HTMLInputElement>) => {
    onFiles(Array.from(event.target.files ?? []));
    event.target.value = "";
  };
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (!disabled) onFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <div className="cp-plan-document-dropzone-wrap">
      <div
        className="cp-plan-document-dropzone"
        data-active={dragActive}
        data-disabled={disabled}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setDragActive(false);
          }
        }}
        onDrop={handleDrop}
        aria-label={`Upload ${displayName}`}
        onClick={() => {
          if (!disabled) inputRef.current?.click();
        }}
        onKeyDown={(event) => {
          if (disabled || (event.key !== "Enter" && event.key !== " ")) return;
          event.preventDefault();
          inputRef.current?.click();
        }}
      >
        <FileUp aria-hidden="true" />
        <div>
          <strong>Drop a file here or click to choose</strong>
          <span>PDF, DOCX, or TXT. Maximum 10 MiB.</span>
        </div>
      </div>
      <input
        ref={inputRef}
        className="cp-visually-hidden"
        type="file"
        accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        disabled={disabled}
        aria-label={`Choose ${displayName} file`}
        onChange={handleSelection}
      />
      {onCancel ? (
        <button
          type="button"
          className="cp-button cp-button--secondary"
          disabled={disabled}
          onClick={onCancel}
        >
          Cancel
        </button>
      ) : null}
    </div>
  );
}

function PlanDocumentSlot({
  definition,
  document,
  clinicianCode,
  patientId,
  onUploaded,
  onDeleted,
  readOnly,
}: {
  definition: (typeof planDefinitions)[number];
  document?: PlanDocument;
  clinicianCode: string;
  patientId: string;
  onUploaded: (document: PlanDocument) => void;
  onDeleted: (planType: PlanType) => void;
  readOnly: boolean;
}) {
  const mutation = usePortalMutation();
  const inFlightRef = useRef(false);
  const [replacing, setReplacing] = useState(false);
  const [dragError, setDragError] = useState<string>();
  const [cleanupWarning, setCleanupWarning] = useState<string>();
  const [confirmation, setConfirmation] = useState<ConfirmationState>(null);
  const busy = mutation.state.status === "pending" || inFlightRef.current;
  const status = statusPresentation(document);
  const updatedAt = document ? formatUpdatedAt(document.updatedAt) : "";
  const filename = document
    ? document.originalFilename.trim() || "Indexed legacy document"
    : "";

  const upload = async (file: File) => {
    if (readOnly || inFlightRef.current) return;
    inFlightRef.current = true;
    setDragError(undefined);
    setCleanupWarning(undefined);
    setConfirmation(null);
    try {
      const response = await mutation.run("upload-plan-document", async (signal) => {
        const contentBase64 = await planDocumentFileToBase64(file);
        return clientPlanDocumentsApi.uploadPlanDocument(
          {
            clinicianCode,
            patientId,
            planType: definition.planType,
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            contentBase64,
          },
          signal,
        );
      });
      if (!response) {
        setDragError(
          document
            ? `The ${definition.displayName} could not be replaced. The current document is unchanged.`
            : `The ${definition.displayName} could not be uploaded. Please try again.`,
        );
        return;
      }
      setReplacing(false);
      onUploaded(response.document);
    } finally {
      inFlightRef.current = false;
    }
  };

  const acceptFiles = (files: readonly File[]) => {
    if (readOnly || inFlightRef.current) return;
    setDragError(undefined);
    if (files.length !== 1) {
      setDragError("Choose exactly one document for this plan type.");
      return;
    }
    const file = files[0];
    const validationMessage = planDocumentValidationMessage(file);
    if (validationMessage) {
      setDragError(validationMessage);
      return;
    }
    if (document) {
      setConfirmation({ kind: "replace", file });
      return;
    }
    void upload(file);
  };

  const remove = async () => {
    if (readOnly || inFlightRef.current) return;
    inFlightRef.current = true;
    setDragError(undefined);
    setCleanupWarning(undefined);
    setConfirmation(null);
    try {
      const response = await mutation.run("delete-plan-document", (signal) =>
        clientPlanDocumentsApi.deletePlanDocument(
          clinicianCode,
          patientId,
          definition.planType,
          signal,
        ),
      );
      if (!response) {
        setDragError(
          `The ${definition.displayName} could not be deleted. The current document is unchanged.`,
        );
        return;
      }
      onDeleted(definition.planType);
      if (response.cleanupWarnings.length) {
        setCleanupWarning(
          "The document was deleted, but some background cleanup could not be completed.",
        );
      }
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <article className="cp-plan-document-row" aria-busy={busy}>
      <div className="cp-plan-document-row__summary">
        <div className="cp-plan-document-row__actions">
          {document && !readOnly ? (
            <RowActionMenu
              clinicianCode={clinicianCode}
              activityName={definition.displayName}
              canEdit={!busy}
              onEdit={() => {
                mutation.reset();
                setDragError(undefined);
                setReplacing(true);
              }}
              editLabel="Replace"
              showDuplicate={false}
              canDelete={!busy}
              isDeleting={
                mutation.state.status === "pending" &&
                mutation.state.action === "delete-plan-document"
              }
              onDelete={() => setConfirmation({ kind: "delete" })}
            />
          ) : null}
        </div>
        <div className="cp-plan-document-row__identity">
          <div className="cp-plan-document-row__title">
            <h4>{definition.displayName}</h4>
            <PlanDocumentHelp definition={definition} />
          </div>
        </div>
        <div className="cp-plan-document-row__status">
          <span className="cp-plan-document-status" data-tone={status.tone}>
            {status.label}
          </span>
        </div>
        {document ? (
          <div className="cp-plan-document-row__file">
            <p className="cp-plan-document-row__filename" title={filename}>
              {filename}
            </p>
            {updatedAt ? (
              <span className="cp-plan-document-row__updated">
                Updated {updatedAt}
              </span>
            ) : null}
            {status.tone === "failure" ? (
              <p className="cp-plan-document-row__file-error">
                {document.errorMessage.trim() || "Document processing failed."}
              </p>
            ) : null}
          </div>
        ) : readOnly ? (
          <div className="cp-plan-document-row__file">
            <span className="cp-empty-value">No document</span>
          </div>
        ) : (
          <div className="cp-plan-document-row__upload">
            <PlanDocumentDropzone
              displayName={definition.displayName}
              disabled={busy}
              onFiles={acceptFiles}
            />
          </div>
        )}
      </div>

      {document && replacing && !readOnly ? (
        <PlanDocumentDropzone
          displayName={definition.displayName}
          disabled={busy}
          onFiles={acceptFiles}
          onCancel={
            document
              ? () => {
                  setReplacing(false);
                  setDragError(undefined);
                  setConfirmation(null);
                }
              : undefined
          }
        />
      ) : null}

      {busy ? (
        <p className="cp-plan-document-row__pending" role="status">
          {mutation.state.status === "pending" &&
          mutation.state.action === "delete-plan-document"
            ? "Deleting…"
            : "Uploading and indexing…"}
        </p>
      ) : null}
      {dragError ? (
        <p className="cp-form-error" role="alert">
          {dragError}
        </p>
      ) : null}
      {cleanupWarning ? (
        <p className="cp-inline-notice cp-plan-document-row__notice" role="status">
          {cleanupWarning}
        </p>
      ) : null}

      {confirmation ? (
        <PlanDocumentConfirmationDialog
          confirmation={confirmation}
          displayName={definition.displayName}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            if (confirmation.kind === "replace") {
              void upload(confirmation.file);
            } else {
              void remove();
            }
          }}
        />
      ) : null}
    </article>
  );
}

function loadErrorMessage(error: unknown): string {
  if (!(error instanceof ClientPlanDocumentsApiError)) {
    return "Plan documents could not be loaded.";
  }
  if (error.code === "NOT_AUTHORIZED") {
    return "You are not authorized to view plan documents for this client.";
  }
  if (error.code === "NOT_FOUND") return "This client could not be found.";
  if (error.code === "INVALID_RESPONSE") {
    return "The plan documents service returned an unexpected response.";
  }
  return "Plan documents are temporarily unavailable.";
}

function PlanDocumentsError({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="cp-state cp-state--error" role="alert">
      <h2>Unable to load Plans &amp; Documents</h2>
      <p>{loadErrorMessage(error)}</p>
      <button
        type="button"
        className="cp-button cp-button--secondary"
        onClick={onRetry}
      >
        Try again
      </button>
    </div>
  );
}

function ExposureHierarchyTextEditor({
  clinicianCode,
  patientId,
  initialValue,
  onSaved,
}: {
  clinicianCode: string;
  patientId: string;
  initialValue: string;
  onSaved: (patient: PatientPlanDocumentSummary) => void;
}) {
  const [savedValue, setSavedValue] = useState(initialValue);
  const [draftValue, setDraftValue] = useState(initialValue);
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  const mutation = usePortalMutation();

  useEffect(() => {
    setSavedValue(initialValue);
    if (!editingRef.current) setDraftValue(initialValue);
  }, [initialValue]);

  const setEditMode = (value: boolean) => {
    editingRef.current = value;
    setEditing(value);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mutation.state.status === "pending") return;
    const response = await mutation.run("save-exposure-hierarchy-text", (signal) =>
      clientPlanDocumentsApi.updateExposureHierarchyText(
        clinicianCode,
        patientId,
        draftValue,
        signal,
      ),
    );
    if (!response) return;
    setSavedValue(response.patient.exposureHierarchyText);
    setDraftValue(response.patient.exposureHierarchyText);
    setEditMode(false);
    onSaved(response.patient);
  };

  const handleCancel = () => {
    setDraftValue(savedValue);
    mutation.reset();
    setEditMode(false);
  };

  return (
    <div className="cp-exposure-hierarchy-text">
      <div className="cp-exposure-hierarchy-text__header">
        <div className="cp-exposure-hierarchy-text__title">
          <h3>Exposure Hierarchy Text</h3>
          <ExposureHierarchyTextHelp variant="guidance" />
          <ExposureHierarchyTextHelp variant="example" />
        </div>
        {!editing ? (
          <button
            type="button"
            className="cp-button cp-button--secondary"
            onClick={() => {
              setDraftValue(savedValue);
              mutation.reset();
              setEditMode(true);
            }}
          >
            Edit
          </button>
        ) : null}
      </div>
      <p
        id="exposure-hierarchy-text-helper"
        className="cp-exposure-hierarchy-text__helper"
      >
        Enter or paste a text version of the <strong>exposure hierarchy</strong>{" "}
        attached above.
      </p>
      {editing ? (
        <form onSubmit={handleSave}>
          <label
            className="cp-visually-hidden"
            htmlFor="exposure-hierarchy-text"
          >
            Exposure Hierarchy Text
          </label>
          <textarea
            id="exposure-hierarchy-text"
            className="cp-form-input cp-form-textarea cp-exposure-hierarchy-text__editor"
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
            aria-describedby="exposure-hierarchy-text-helper"
            placeholder={
              "10:\nEating food that touched the floor\nHolding a knife near Mom\n\n9:\nEating food off the table"
            }
            disabled={mutation.state.status === "pending"}
          />
          {mutation.state.status === "error" ? (
            <div className="cp-inline-notice cp-inline-notice--error" role="alert">
              The Exposure Hierarchy text could not be saved. Please try again.
            </div>
          ) : null}
          <div className="cp-exposure-hierarchy-text__actions">
            <button
              type="button"
              className="cp-button cp-button--secondary"
              onClick={handleCancel}
              disabled={mutation.state.status === "pending"}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cp-button cp-button--primary"
              disabled={mutation.state.status === "pending"}
            >
              {mutation.state.status === "pending" ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      ) : savedValue ? (
        <p className="cp-exposure-hierarchy-text__value">{savedValue}</p>
      ) : (
        <p className="cp-empty-value">Not provided</p>
      )}
    </div>
  );
}

function ClientPlanDocumentsContent({
  clinicianCode,
  patientId,
  onContextLoaded,
  readOnly,
  accessScope,
}: {
  clinicianCode: string;
  patientId: string;
  onContextLoaded?: (patient: PatientPlanDocumentSummary) => void;
  readOnly: boolean;
  accessScope: ClientAccessScope;
}) {
  const request = useCallback(
    () => listPlanDocumentsOnce(clinicianCode, patientId, accessScope),
    [accessScope, clinicianCode, patientId],
  );
  const state = usePortalData(request);
  const [savedPatient, setSavedPatient] =
    useState<PatientPlanDocumentSummary>();
  const [documents, setDocuments] = useState<PlanDocument[]>();
  const [columnWidths, setColumnWidths] =
    useState<PlanDocumentColumnWidths>(getInitialPlanDocumentColumnWidths);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const displayedPatient = savedPatient ?? data?.patient;

  useEffect(() => {
    if (!data?.patient) return;
    setSavedPatient(data.patient);
    onContextLoaded?.(data.patient);
  }, [data?.patient, onContextLoaded]);

  useEffect(() => {
    if (data?.planDocuments) setDocuments(data.planDocuments);
  }, [data?.planDocuments]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        PLAN_DOCUMENT_COLUMN_WIDTHS_STORAGE_KEY,
        JSON.stringify(columnWidths),
      );
    } catch {
      // Column resizing still works when browser storage is unavailable.
    }
  }, [columnWidths]);

  const resizeColumn = (
    column: ResizablePlanDocumentColumn,
    width: number,
  ) => {
    setColumnWidths((current) => ({
      ...current,
      [column]: clampPlanDocumentColumnWidth(column, width),
    }));
  };

  const currentDocuments = useMemo(() => {
    const visibleDocuments = documents ?? data?.planDocuments ?? [];
    return new Map(
      planDefinitions.map(({ planType }) => [
        planType,
        visibleDocuments.find(
          (document) => document.planType === planType && document.isCurrent,
        ),
      ]),
    );
  }, [data?.planDocuments, documents]);

  if (state.status === "loading") {
    return <LoadingState label="Loading Plans & Documents" />;
  }
  if (state.status === "error") {
    return <PlanDocumentsError error={state.error} onRetry={state.refresh} />;
  }
  if (!data || !displayedPatient) {
    return <PlanDocumentsError error={undefined} onRetry={state.refresh} />;
  }

  const effectiveReadOnly = readOnly || data.access?.readOnly === true;

  return (
    <ContentSection
      title="Plans & Documents"
      hideTitle
      actions={
        <button
          type="button"
          className="cp-button cp-button--secondary"
          onClick={state.refresh}
          disabled={state.status === "refreshing"}
        >
          <RefreshCw
            className={state.status === "refreshing" ? "cp-spin" : undefined}
            aria-hidden="true"
          />
          Refresh
        </button>
      }
    >
      <p className="cp-plan-documents-description">
        Client plans include <strong>Safety Plan</strong>,{" "}
        <strong>Exposure Hierarchy</strong>,{" "}
        <strong>Relapse Prevention Plan</strong>, and{" "}
        <strong>Cope Ahead Plan</strong>.
      </p>
      <div
        className="cp-plan-documents-replacement-guidance"
        role="note"
        aria-label="Document replacement guidance"
      >
        <span className="cp-plan-documents-replacement-guidance__icon">
          <Info aria-hidden="true" />
        </span>
        <p>
          <strong>Replacing a document?</strong>{" "}
          {PLAN_DOCUMENT_REPLACEMENT_GUIDANCE}
        </p>
      </div>
      <div className="cp-plan-documents">
        <div
          className="cp-plan-document-list"
          style={
            {
              "--cp-plan-document-column-width": `${columnWidths.document}px`,
              "--cp-plan-document-status-column-width": `${columnWidths.status}px`,
            } as React.CSSProperties
          }
        >
          <div className="cp-plan-document-list__header">
            <span className="cp-plan-document-list__actions-header">
              {effectiveReadOnly ? null : <ActionsColumnHeader />}
            </span>
            <div className="cp-plan-document-list__header-cell">
              <span>Document</span>
              <PlanDocumentColumnResizeHandle
                column="document"
                label="Document"
                width={columnWidths.document}
                onChange={(width) => resizeColumn("document", width)}
              />
            </div>
            <div className="cp-plan-document-list__header-cell">
              <span>Status</span>
              <PlanDocumentColumnResizeHandle
                column="status"
                label="Status"
                width={columnWidths.status}
                onChange={(width) => resizeColumn("status", width)}
              />
            </div>
            <span>File / Upload</span>
          </div>
          {planDefinitions.map((definition) => (
            <PlanDocumentSlot
              key={definition.planType}
              definition={definition}
              document={currentDocuments.get(definition.planType)}
              clinicianCode={clinicianCode}
              patientId={patientId}
              onUploaded={(uploadedDocument) => {
                setDocuments((current) => [
                  ...(current ?? data.planDocuments).filter(
                    (item) => item.planType !== uploadedDocument.planType,
                  ),
                  uploadedDocument,
                ]);
                state.refresh();
              }}
              onDeleted={(planType) => {
                setDocuments((current) =>
                  (current ?? data.planDocuments).filter(
                    (item) => item.planType !== planType,
                  ),
                );
                state.refresh();
              }}
              readOnly={effectiveReadOnly}
            />
          ))}
        </div>
        {effectiveReadOnly ? (
          <div className="cp-exposure-hierarchy-text">
            <h3>Exposure Hierarchy Text</h3>
            {displayedPatient.exposureHierarchyText ? (
              <p className="cp-exposure-hierarchy-text__value">
                {displayedPatient.exposureHierarchyText}
              </p>
            ) : (
              <p className="cp-empty-value">Not provided</p>
            )}
          </div>
        ) : <ExposureHierarchyTextEditor
          clinicianCode={clinicianCode}
          patientId={patientId}
          initialValue={displayedPatient.exposureHierarchyText}
          onSaved={(patient) => {
            setSavedPatient(patient);
            onContextLoaded?.(patient);
          }}
        />}
      </div>
    </ContentSection>
  );
}

export function ClientPlanDocumentsSection({
  clinicianCode,
  patientId,
  onContextLoaded,
  readOnly = false,
  accessScope = "assigned",
}: {
  clinicianCode: string;
  patientId: string;
  onContextLoaded?: (patient: PatientPlanDocumentSummary) => void;
  readOnly?: boolean;
  accessScope?: ClientAccessScope;
}) {
  const normalizedClinicianCode = clinicianCode.trim();
  const normalizedPatientId = patientId.trim();

  if (!normalizedClinicianCode || !normalizedPatientId) {
    return <LoadingState label="Loading Plans & Documents" />;
  }
  return (
    <ClientPlanDocumentsContent
      key={`${normalizedClinicianCode}:${normalizedPatientId}:${accessScope}`}
      clinicianCode={normalizedClinicianCode}
      patientId={normalizedPatientId}
      onContextLoaded={onContextLoaded}
      readOnly={readOnly}
      accessScope={accessScope}
    />
  );
}
